/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

import { FILE_EXTENSIONS, MediaType, velocitiOrganizationId } from '../../../Constants/App';
import Button, { ButtonType } from '../Buttons/Button';
import { Icons } from '../../../Constants/Icons';

import {
  DialogWrapper,
  CloseIconStyle,
  PopupHeadingText,
  PhotosCountText,
  ChevronStyle,
  buttonStyle,
} from './MediaPopUp.Styles';

interface MediaPopupProps {
  isOpen: boolean;
  onClose: () => void;
  currentMediaIndex: number;
  media: any[];
  showPreviousMedia: () => void;
  showNextMedia: () => void;
  numMedia: number;
  type: 'photo' | 'video' | 'pdf' | 'document';
  scWorkOrderId?: string | null;
  onSignedApprovalToggle?: () => Promise<void>;
  approvalButtonLabel?: string;
  organizationId?: string;
}

const MediaPopup: React.FC<MediaPopupProps> = ({
  isOpen,
  onClose,
  currentMediaIndex,
  media,
  showPreviousMedia,
  showNextMedia,
  numMedia,
  type,
  scWorkOrderId,
  onSignedApprovalToggle,
  approvalButtonLabel,
  organizationId,
}) => {
  const { t } = useTranslation(['altTexts']);
  const { photoAndVideoStyle } = buttonStyle;

  const [isApprovalLoading, setIsApprovalLoading] = useState(false);

  const handleDownloadMedia = (mediaUrl: string) => {
    window.open(mediaUrl);
  };

  const handleApprovalClick = async () => {
    if (!onSignedApprovalToggle) return;
    setIsApprovalLoading(true);
    try {
      await onSignedApprovalToggle();
    } finally {
      setIsApprovalLoading(false);
    }
  };

  const currentUrl = (media[currentMediaIndex]?.url as string) ?? '';

  const headingText =
    type === MediaType.Photo
      ? t('dashboard:photos')
      : type === MediaType.Video
        ? t('dashboard:videos')
        : t('dashboard:documents');

  const renderMedia = () => {
    if (type === MediaType.Photo) {
      return <img src={currentUrl} alt={t('altTexts:image')} style={{ ...photoAndVideoStyle, objectFit: 'contain' }} />;
    }
    if (type === MediaType.Video) {
      return <video controls src={currentUrl} style={{ ...photoAndVideoStyle, objectFit: 'contain' }} />;
    }
    const currentExtension = media[currentMediaIndex]?.fileExtension?.toLowerCase();
    const iframeSrc =
      currentExtension === FILE_EXTENSIONS.docx
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(currentUrl)}&embedded=true`
        : `${currentUrl}#toolbar=1&navpanes=1&scrollbar=1`;

    return (
      <iframe
        src={iframeSrc}
        title="document-viewer"
        style={{
          ...photoAndVideoStyle,
          border: 'none',
          borderRadius: '0.25rem',
        }}
      />
    );
  };

  return (
    <DialogWrapper open={isOpen} onClose={onClose}>
      <CloseIconStyle onClick={onClose}>
        <img src={Icons.InfoWindowCloseIcon} alt={t('altTexts:closeIcon')} />
      </CloseIconStyle>
      <PopupHeadingText
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
        {headingText}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => handleDownloadMedia(currentUrl)}>
          <img src={Icons.DownloadBlackIcon} alt={t('altTexts:download')} />
        </Box>
      </PopupHeadingText>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'relative' }}>
          {renderMedia()}
          {numMedia > 1 && (
            <>
              <ChevronStyle sx={{ left: 0 }} onClick={showPreviousMedia}>
                <img src={Icons.LeftChevronIcon} alt={t('altTexts:backArrow')} />
              </ChevronStyle>
              <ChevronStyle sx={{ right: 0 }} onClick={showNextMedia}>
                <img src={Icons.RightChevronIcon} alt={t('altTexts:rightArrow')} />
              </ChevronStyle>
            </>
          )}
          <Box sx={{ textAlign: 'center', marginTop: '0.5rem' }}>
            <PhotosCountText>
              {currentMediaIndex + 1}/{numMedia}
            </PhotosCountText>
          </Box>
        </Box>
      </Box>

      {scWorkOrderId && organizationId === velocitiOrganizationId && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
          <Button
            onClick={handleApprovalClick}
            label={approvalButtonLabel}
            type={ButtonType.Primary}
            isLoading={isApprovalLoading}
            disabled={isApprovalLoading}
          />
        </Box>
      )}
    </DialogWrapper>
  );
};

export default MediaPopup;
